import { useState, useCallback } from "react";
import { STANDE, STANDE_DARK, ON_TINT } from "../StandeClassroom/standeTokens";

// Light/dark toggle for the main Stande app (papers dashboard). Persisted so it
// survives navigation and reloads. Kept separate from the classroom's own mode.
export function useAppColorMode() {
  const [isDark, setIsDark] = useState(
    () => localStorage.getItem("appColorMode") === "dark"
  );
  const toggle = useCallback(() => {
    setIsDark((prev) => {
      const next = !prev;
      localStorage.setItem("appColorMode", next ? "dark" : "light");
      return next;
    });
  }, []);
  return { isDark, toggle };
}

// Literal palettes (for portalled Chakra bits like Menus). The brand red-orange
// accent and the material blue stay constant across modes.
export const PALETTES = {
  light: {
    page: "#ffffff",
    chrome: "#F8F7F4",
    surface: "#ffffff",
    surfaceSubtle: "#F8F7F4",
    border: "#E7EAE8",
    borderSoft: "#EDEDED",
    text: "#171717",
    text2: "#343634",
    textSub: "#535953",
    textMuted: "#8a8a8a",
    hover: STANDE.grey,
    accent: "#FF4716",
    accentHover: "#E63D0F",
    accentSoft: "#FFEEEA",
    blue: "#9AE9FE",
    onAccent: "#ffffff",
    inputBg: "#ffffff",
    inputBorder: "#CFCFCF",
    overlay: "rgba(19,17,20,0.45)",
    materialBg: "#9AE9FE",
    materialText: "#1C2E1D",
    pillBg: "#ffffff",
    pillText: "#171717",
    // The paper and the question list are a shade off the page, so they read
    // as sheets laid on it rather than holes cut out of it.
    panel: "#fdfdfb",
    panelBorder: "rgba(0,0,0,0.06)",
    // A hairline drawn over a card: the ink at low alpha, never a new grey.
    edge: "rgba(34,34,34,0.16)",
    // The inversion a finished question uses.
    ink: STANDE.black,
    onInk: STANDE.cream,
    railTodo: STANDE.grey,
    // stande-grey-light: the ground for work already handed in, so a
    // finished answer reads as settled rather than as another empty field,
    // without sitting as heavily on the page as stande-grey did.
    greySurface: STANDE.greyLight,
    tintGreen: STANDE.lightGreen,
    tintYellow: STANDE.lightYellow,
    tintRed: STANDE.lightRed,
    // A 6px block and a 9px chip need different treatment. On a pale ground
    // the tint is enough for both; on a dark one a tint turns to mud, so the
    // rail takes the strong colour and the chip a wash of it.
    railOk: STANDE.darkGreen,
    railWarn: STANDE.darkYellow,
    railBad: STANDE.darkRed,
    chipOkBg: STANDE.lightGreen,
    chipWarnBg: STANDE.lightYellow,
    chipBadBg: STANDE.lightRed,
    chipOkEdge: "rgba(13,207,7,0.45)",
    chipWarnEdge: "rgba(236,195,10,0.55)",
    chipBadEdge: "rgba(255,74,74,0.45)",
    // Feedback correctness tints: correct -> green, partial -> amber,
    // incorrect -> red (kept as before; only dark-mode variants are new).
    okBg: STANDE.lightGreen,
    okText: ON_TINT.light.lightGreen,
    warnBg: STANDE.lightYellow,
    warnText: ON_TINT.light.lightYellow,
    badBg: STANDE.lightRed,
    badText: ON_TINT.light.lightRed,
  },
  // On the same neutral line as the classroom's dark mode: every step is
  // pure grey between stande-black and white. The previous values carried a
  // purple cast (#131114, #322e38, #b6b1bd), which is the tint the brand
  // does not own and which the classroom was already moved off.
  dark: {
    page: "#121212",
    chrome: "#1a1a1a",
    surface: "#1a1a1a",
    surfaceSubtle: "#222222",
    border: "#2e2e2e",
    borderSoft: "#262626",
    text: "#f5f5f5",
    text2: "#ebebeb",
    textSub: "#b4b4b4",
    textMuted: "#8a8a8a",
    hover: "#262626",
    accent: "#FF4716",
    accentHover: "#ff6a44",
    accentSoft: "#3a2620",
    blue: "#9AE9FE",
    onAccent: "#ffffff",
    inputBg: "#161616",
    inputBorder: "#3a3a3a",
    overlay: "rgba(0,0,0,0.6)",
    materialBg: "#262626",
    materialText: "#f5f5f5",
    pillBg: "#9AE9FE",
    pillText: "#000000",
    panel: "#191919",
    panelBorder: "#2a2a2a",
    edge: "rgba(245,245,245,0.16)",
    ink: STANDE_DARK.black,
    onInk: "#121212",
    railTodo: "#2e2e2e",
    greySurface: STANDE_DARK.greyLight,
    tintGreen: STANDE_DARK.lightGreen,
    tintYellow: STANDE_DARK.lightYellow,
    tintRed: STANDE_DARK.lightRed,
    railOk: STANDE_DARK.darkGreen,
    railWarn: STANDE_DARK.darkYellow,
    railBad: STANDE_DARK.darkRed,
    chipOkBg: "rgba(134,226,154,0.14)",
    chipWarnBg: "rgba(242,196,113,0.14)",
    chipBadBg: "rgba(255,159,146,0.14)",
    chipOkEdge: "rgba(134,226,154,0.38)",
    chipWarnEdge: "rgba(242,196,113,0.38)",
    chipBadEdge: "rgba(255,159,146,0.38)",
    okBg: STANDE_DARK.lightGreen,
    okText: ON_TINT.dark.lightGreen,
    warnBg: STANDE_DARK.lightYellow,
    warnText: ON_TINT.dark.lightYellow,
    badBg: STANDE_DARK.lightRed,
    badText: ON_TINT.dark.lightRed,
  },
};

// CSS-variable tokens with the LIGHT value as fallback, so anything using them
// renders identically in light mode. Inside `.stande-app[data-theme="dark"]`
// (see app-theme.css) the variables flip everything to dark.
const L = PALETTES.light;
export const a = {
  page: `var(--app-page, ${L.page})`,
  chrome: `var(--app-chrome, ${L.chrome})`,
  surface: `var(--app-surface, ${L.surface})`,
  surfaceSubtle: `var(--app-surface-subtle, ${L.surfaceSubtle})`,
  border: `var(--app-border, ${L.border})`,
  borderSoft: `var(--app-border-soft, ${L.borderSoft})`,
  text: `var(--app-text, ${L.text})`,
  text2: `var(--app-text2, ${L.text2})`,
  textSub: `var(--app-text-sub, ${L.textSub})`,
  textMuted: `var(--app-text-muted, ${L.textMuted})`,
  hover: `var(--app-hover, ${L.hover})`,
  accent: `var(--app-accent, ${L.accent})`,
  accentHover: `var(--app-accent-hover, ${L.accentHover})`,
  accentSoft: `var(--app-accent-soft, ${L.accentSoft})`,
  blue: `var(--app-blue, ${L.blue})`,
  onAccent: `var(--app-on-accent, ${L.onAccent})`,
  inputBg: `var(--app-input-bg, ${L.inputBg})`,
  inputBorder: `var(--app-input-border, ${L.inputBorder})`,
  materialBg: `var(--app-material-bg, ${L.materialBg})`,
  materialText: `var(--app-material-text, ${L.materialText})`,
  pillBg: `var(--app-pill-bg, ${L.pillBg})`,
  panel: `var(--app-panel, ${L.panel})`,
  panelBorder: `var(--app-panel-border, ${L.panelBorder})`,
  edge: `var(--app-edge, ${L.edge})`,
  ink: `var(--app-ink, ${L.ink})`,
  onInk: `var(--app-on-ink, ${L.onInk})`,
  railTodo: `var(--app-rail-todo, ${L.railTodo})`,
  greySurface: `var(--app-grey-surface, ${L.greySurface})`,
  tintGreen: `var(--app-tint-green, ${L.tintGreen})`,
  tintYellow: `var(--app-tint-yellow, ${L.tintYellow})`,
  tintRed: `var(--app-tint-red, ${L.tintRed})`,
  railOk: `var(--app-rail-ok, ${L.railOk})`,
  railWarn: `var(--app-rail-warn, ${L.railWarn})`,
  railBad: `var(--app-rail-bad, ${L.railBad})`,
  chipOkBg: `var(--app-chip-ok-bg, ${L.chipOkBg})`,
  chipWarnBg: `var(--app-chip-warn-bg, ${L.chipWarnBg})`,
  chipBadBg: `var(--app-chip-bad-bg, ${L.chipBadBg})`,
  chipOkEdge: `var(--app-chip-ok-edge, ${L.chipOkEdge})`,
  chipWarnEdge: `var(--app-chip-warn-edge, ${L.chipWarnEdge})`,
  chipBadEdge: `var(--app-chip-bad-edge, ${L.chipBadEdge})`,
  pillText: `var(--app-pill-text, ${L.pillText})`,
  // Segmented tab (Past Papers / Topical): blue in light, white in dark; text
  // always black. Inactive tabs use surface/text/border tokens.
  tabActiveBg: `var(--app-tab-active-bg, ${L.blue})`,
  // Feedback correctness tints.
  okBg: `var(--app-ok-bg, ${L.okBg})`,
  okText: `var(--app-ok-text, ${L.okText})`,
  warnBg: `var(--app-warn-bg, ${L.warnBg})`,
  warnText: `var(--app-warn-text, ${L.warnText})`,
  badBg: `var(--app-bad-bg, ${L.badBg})`,
  badText: `var(--app-bad-text, ${L.badText})`,
};
